import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Building2,
  ChartColumn,
  ChevronLeft,
  Columns3,
  Contact,
  Eye,
  FileText,
  Globe,
  LogIn,
  Mail,
  Mails,
  MessagesSquare,
  Palette,
  PanelRight,
  Phone,
  RotateCcw,
  Send,
  Server,
  ShieldCheck,
  TriangleAlert,
  Type,
  Video,
  Wallpaper,
  Image as ImageIcon,
} from 'lucide-react'
import Button from '../../components/Button'
import Checkbox from '../../components/Checkbox'
import FileField from '../../components/FileField'
import { InfoNote } from '../../components/Form'
import PromptDialog from '../../components/PromptDialog'
import Select from '../../components/Select'
import { DeniedState, ErrorState, LoadingState } from '../../components/States'
import TextField from '../../components/TextField'
import { useToast } from '../../components/toastContext'
import { normalizeHex } from '../../components/colorUtils'
import { DEFAULT_ACTIVE, DEFAULT_MAIN, saveBranding } from '../../app/branding'
import { serverDetail, serverText } from '../../lib/i18n'
import { useSession } from '../../lib/sessionContext'
import { logoBackgroundEnabled } from '../../components/logoBackground'
import BrandThemeCard from './BrandThemeCard'
import DocumentationCard from './DocumentationCard'
import SettingsTabs from './SettingsTabs'
import { Block, Matrix, Pair, PublishedFile, ReadValue, Row, SwitchRow } from './SettingsParts'
import { MAIN_SECTIONS, mainInitialForm, mainKeys } from './mainForm'
import { sectionIdsFor } from './settingsNav'
import { getSettings, saveSection, testEmailSettings } from './settingsService'
import useSettingsForm, { useSection } from './useSettingsForm'
import { common as C, settingsStrings as S } from './strings'
import './settings.css'
import { displayDate } from '../../lib/dates'

/* The sections this page opens, from the one map every settings page shares. */
const SECTION_IDS = sectionIdsFor('main')


/**
 * WHERE EACH UPLOAD ENDS UP.
 *
 * Every one of these endpoints writes to a FIXED name — `SaveLogo` to
 * `Rawam-logo-with-text2.png`, `SaveFavIcon` to `favicon.ico`, and so on — and the app
 * serves them straight from the web root. So the current file is knowable without the
 * payload carrying a URL, and the picker can show what is in place beside what is about
 * to replace it. `ground` is what to draw a transparent image on.
 */
const MEDIA = {
  logo: { url: '/images/Rawam-logo-with-text2.png', ground: 'dark' },
  reportLogo: { url: '/images/Rawam-logo-reporting.png', ground: 'checker' },
  sideLoginAr: { url: '/images/login-side-ar.png', ground: 'light' },
  sideLoginEn: { url: '/images/login-side-en.png', ground: 'light' },
  backgroundAr: { url: '/images/login-ar.png', ground: 'light' },
  backgroundEn: { url: '/images/login-en.png', ground: 'light' },
  favicon: { url: '/favicon.ico', ground: 'checker' },
}

/* MailKit's `SecureSocketOptions`, by name — what `SaveEmailSettings` accepts. */
const SSL_OPTIONS = ['None', 'Auto', 'SslOnConnect', 'StartTls', 'StartTlsWhenAvailable'].map((value) => ({
  value,
  label: S.email.sslOptions[value],
}))

const THEMES = [
  { value: 'white', label: S.media.white },
  { value: 'black', label: S.media.black },
]

/** The three notification channels — one stored JSON blob and one save action each. */
const CHANNELS = [
  {
    id: 'whatsapp',
    enabledKey: 'whatsappEnabled',
    fields: [
      { key: 'whatsappApiUrl', label: 'apiUrl' },
      { key: 'whatsappApiKey', label: 'apiKey' },
      { key: 'whatsappSender', label: 'senderNumber' },
    ],
  },
  {
    id: 'slack',
    enabledKey: 'slackEnabled',
    fields: [
      { key: 'slackWebhook', label: 'webhookUrl' },
      { key: 'slackBotToken', label: 'botToken' },
      { key: 'slackChannel', label: 'channel' },
    ],
  },
  {
    id: 'sms',
    enabledKey: 'smsEnabled',
    fields: [
      { key: 'smsApiUrl', label: 'apiUrl' },
      { key: 'smsApiKey', label: 'apiKey' },
      { key: 'smsSenderId', label: 'senderId' },
    ],
  },
]

const asDate = (iso) => displayDate(iso)

/* The same address after a save holds a different picture, so it needs a different
   address — a browser that cached the old logo would otherwise go on showing it. */
const stamped = (url, version) => (version ? `${url}?v=${version}` : url)

/** The brand pair goes to its action as `{ main, active }`. */
const brandOf = (form) => ({
  main: normalizeHex(form.brandMain) || DEFAULT_MAIN,
  active: normalizeHex(form.brandActive) || DEFAULT_ACTIVE,
})

const saveGroup = (group, form) =>
  group === 'brandTheme' ? saveSection('brandTheme', brandOf(form)) : saveSection(group, form)

/* Once the server has the pair, this browser's own copy follows — see app/branding. */
const afterSave = (group, form) => {
  if (group === 'brandTheme') saveBranding(brandOf(form))
}

/** إعدادات النظام — Views/Settings/Index.cshtml. */
export default function SettingsPage() {
  const toast = useToast()
  const { revalidate } = useSession()
  const nav = useSection('identity', SECTION_IDS)
  const current = nav.current
  const [data, setData] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [testing, setTesting] = useState(false)
  const [askingTest, setAskingTest] = useState(false)

  const settings = useSettingsForm({ sections: MAIN_SECTIONS, save: saveGroup, afterSave: (group, saved) => {
    afterSave(group, saved)
    if (group === 'logo') void revalidate()
    /* A new guide replaces the one listed under its picker; only that list is re-read, so
       nothing else on the page is put back to what the server had. */
    if (group === 'userGuide') {
      getSettings()
        .then((fresh) => setData((previous) => ({ ...previous, userManuals: fresh.userManuals })))
        .catch(() => {})
    }
  } })
  const { form, set, setValue, start } = settings

  useEffect(() => {
    getSettings()
      .then((response) => {
        setData(response)
        start(mainInitialForm(response), mainKeys(response))
      })
      .catch(setLoadError)
  }, [start])

  /**
   * The probe. It asks WHERE to send first, and sends what is in the form, saved or not:
   * the point is to find out before committing it.
   */
  const runEmailTest = async (address) => {
    setTesting(true)
    const outcome = await testEmailSettings(form, address)
    setTesting(false)
    setAskingTest(false)
    if (outcome.ok) toast.success(serverText(outcome.message) || C.saved)
    else toast.error(serverDetail(outcome.message) || C.saveFailed)
  }

  /** Attribute1..5 wear the name configured under إعدادات متقدمة. */
  const attributeLabel = (attribute) =>
    attribute.label ?? (data?.settings?.[attribute.setting] || attribute.name)

  const toggleColumn = (key, name) => {
    const current = form?.[key] ?? []
    setValue(key, current.includes(name) ? current.filter((item) => item !== name) : [...current, name])
  }

  /** A picker for one of the fixed-address images. */
  /* The file's format and size ride inside the drop zone, where the file goes, rather than
     beside the name (2026-10-22). The white logo previews on what it will sit on: the rail
     itself, or the white plate when "logo background" is on — on transparency it was a
     white mark on a white card, an empty box. */
  const picker = (key, group, accept, id, spec) => (
    <FileField
      key={`${key}-${settings.round(group)}`}
      id={id}
      accept={accept}
      onChange={set(key)}
      currentUrl={stamped(MEDIA[key].url, settings.round(group))}
      previewGround={key === 'logo' ? (logoBackgroundEnabled(form.logoBackground) ? 'white' : 'dark') : MEDIA[key].ground}
      formats={spec ? [spec] : undefined}
    />
  )

  /** A setting written in both languages: one row, both fields. */
  const pair = (label, arKey, enKey) => (
    <Pair
      label={label}
      arabic={form[arKey]}
      english={form[enKey]}
      onArabic={set(arKey)}
      onEnglish={set(enKey)}
    />
  )

  /** A stored `"true"`/`"false"` setting, as a switch row. */
  const textSwitch = (key, label) => (
    <SwitchRow label={label} checked={form[key] === 'true'} onChange={(on) => setValue(key, String(on))} />
  )

  /** A boolean setting, as a switch row. */
  const flag = (key, label) => (
    <SwitchRow label={label} checked={!!form[key]} onChange={set(key)} />
  )

  /** A free-text setting, as a row. Latin unless said otherwise — most are addresses. */
  const text = (key, label, props = {}) => (
    <Row label={label} hint={props.hint}>
      {(fieldId) => (
        <TextField
          id={fieldId}
          value={form[key]}
          onChange={set(key)}
          dir={'dir' in props ? props.dir : 'ltr'}
          type={props.type}
          inputMode={props.inputMode}
          placeholder={props.placeholder}
        />
      )}
    </Row>
  )

  const section = (id) => {
    switch (id) {
      case 'identity':
        return (
          <Block title={S.theme.title} icon={Palette}>
            <BrandThemeCard
              main={form.brandMain}
              active={form.brandActive}
              onMain={set('brandMain')}
              onActive={set('brandActive')}
            />
          </Block>
        )

      case 'media':
        return (
          <>
            {/* Images as a grid of tiles (2026-10-22): each its name over its preview and
                picker, side by side — not a wide row with a short name far from a narrow
                field. */}
            <Block title={S.sheet.logos} icon={ImageIcon}>
              <div className="set-media">
                <Row stack label={S.media.whiteLogo} hint={S.media.whiteLogoHint}>
                  {(fieldId) => picker('logo', 'logo', '.png', fieldId, S.media.whiteLogoSpec)}
                </Row>
                <Row stack label={S.media.mainLogo} hint={S.media.mainLogoHint}>
                  {(fieldId) => picker('reportLogo', 'reportLogo', '.png', fieldId, S.media.whiteLogoSpec)}
                </Row>
                {/* `.ico` as well as `.png`: the endpoint writes whatever it is given to
                    `favicon.ico`, so refusing an actual icon file was the wrong way round. */}
                <Row stack label={S.media.favicon}>
                  {(fieldId) => picker('favicon', 'favicon', '.ico,.png', fieldId, S.media.faviconSpec)}
                </Row>
              </div>
              {textSwitch('logoBackground', S.media.logoBackground)}
            </Block>

            <Block title={S.sheet.signIn} icon={LogIn}>
              <Row label={S.media.loginTheme}>
                {(fieldId) => <Select id={fieldId} value={form.theme} onChange={set('theme')} options={THEMES} />}
              </Row>
              {textSwitch('logoOnLogin', S.media.logoOnLogin)}
            </Block>

            <Block title={S.media.sideImage} icon={PanelRight}>
              <div className="set-media set-media--two">
                <Row stack label={S.media.arabicImage}>
                  {(fieldId) => picker('sideLoginAr', 'sideLogin', '.png', fieldId, S.media.sideImageSpec)}
                </Row>
                <Row stack label={S.media.englishImage}>
                  {(fieldId) => picker('sideLoginEn', 'sideLogin', '.png', fieldId, S.media.sideImageSpec)}
                </Row>
              </div>
            </Block>

            <Block title={S.media.background} icon={Wallpaper}>
              <div className="set-media set-media--two">
                <Row stack label={S.media.imageAr}>
                  {(fieldId) => picker('backgroundAr', 'backgroundLogin', '.jpg,.jpeg', fieldId, S.media.backgroundSpec)}
                </Row>
                <Row stack label={S.media.imageEn}>
                  {(fieldId) => picker('backgroundEn', 'backgroundLogin', '.jpg,.jpeg', fieldId, S.media.backgroundSpec)}
                </Row>
              </div>
            </Block>
          </>
        )

      case 'keys':
        return (
          <>
            <Block title={S.sheet.platform} icon={Type}>
              {pair(S.keys.appTitle, 'arabicAppTitle', 'englishAppTitle')}
              {pair(S.sheet.footer, 'footer', 'footerEn')}
            </Block>
            <Block title={S.keys.organization} icon={Building2}>
              {pair(S.sheet.orgName, 'arabicOrgName', 'englishOrgName')}
              {text('orgEmail', S.keys.orgEmail, { hint: S.keys.orgEmailHint })}
            </Block>
            <Block title={S.sheet.address} icon={Globe}>
              {text('domainUrl', S.keys.domain, { hint: S.keys.domainNote, placeholder: S.keys.domainSpec })}
              {textSwitch('useHsts', S.keys.useHsts)}
              {text('servicesUrl', S.servicesUrl.label, { placeholder: 'https://' })}
            </Block>
          </>
        )

      case 'contact':
        return (
          <>
            <Block
              title={S.contact.enablePage}
              icon={Contact}
              toggle={{ checked: !!form.enableContactPage, onChange: set('enableContactPage') }}
            >
              {!form.enableEmail && !form.showPhone && (
                <InfoNote icon={TriangleAlert}>{S.contact.allDisabledWarning}</InfoNote>
              )}
              {pair(S.contact.pageTitle, 'contactPageTitle', 'contactPageTitleEn')}
              {pair(S.contact.pageHeader, 'contactPageHeader', 'contactPageHeaderEn')}
            </Block>
            {form.enableContactPage && (
              <>
                <Block
                  title={S.contact.enableEmail}
                  icon={Mail}
                  toggle={{ checked: !!form.enableEmail, onChange: set('enableEmail') }}
                >
                  {text('contactEmail', S.contact.emailAddress, { type: 'email' })}
                  {flag('showSendForm', S.contact.showSendForm)}
                  {flag('showEmailInfo', S.contact.showEmailInfo)}
                  {pair(S.contact.emailLabel, 'contactEmailLabel', 'contactEmailEnLabel')}
                </Block>

                <Block
                  title={S.contact.showPhone}
                  icon={Phone}
                  toggle={{ checked: !!form.showPhone, onChange: set('showPhone') }}
                >
                  {text('contactPhone', S.contact.phone)}
                  {pair(S.contact.phoneLabel, 'contactPhoneLabel', 'contactPhoneEnLabel')}
                </Block>
              </>
            )}
          </>
        )

      case 'video':
        return (
          <Block title={S.video.title} icon={Video}>
            {flag('progressBar', S.video.progressBar)}
            {flag('forceSubtitle', S.video.forceSubtitle)}
            {flag('userSubtitleControl', S.video.userSubtitleControl)}
            {flag('pauseOnBlur', S.video.pauseOnBlur)}
            {flag('playbackRates', S.video.playbackRates)}
          </Block>
        )

      case 'userManual': {
        const accept = form.introVideo ? '.mp4' : '.pdf'
        const spec = form.introVideo ? 'MP4' : 'PDF'
        /* The file users would be shown in each language: the one of the kind the intro-video
           switch asks for, as the portal picks it. */
        const published = (language) =>
          (data?.userManuals ?? []).find(
            (manual) => manual.language === language && manual.format === (form.introVideo ? 'mp4' : 'pdf'),
          ) ?? null
        return (
          <>
            <Block
              title={S.sheet.guideFiles}
              icon={FileText}
              aside={
                <Link className="set-link" to="/portal/manual" target="_blank">
                  <bdi>{S.userManual.viewInPortal}</bdi>
                </Link>
              }
            >
              <Row label={S.userManual.guideAr}>
                {(fieldId) => (
                  <>
                    <FileField key={`guideAr-${settings.round('userGuide')}`} id={fieldId} accept={accept} onChange={set('guideAr')} formats={[spec]} />
                    <PublishedFile file={published('ar')} />
                  </>
                )}
              </Row>
              <Row label={S.userManual.guideEn}>
                {(fieldId) => (
                  <>
                    <FileField key={`guideEn-${settings.round('userGuide')}`} id={fieldId} accept={accept} onChange={set('guideEn')} formats={[spec]} />
                    <PublishedFile file={published('en')} />
                  </>
                )}
              </Row>
            </Block>
            <Block title={S.sheet.guideDisplay} icon={Eye}>
              {flag('manualPopup', S.userManual.popup)}
              {flag('introVideo', S.userManual.introVideo)}
              {flag('guideLinkOnLogin', S.userManual.linkOnLogin)}
              {flag('guideSeparatePage', S.userManual.separatePage)}
            </Block>
            <Block title={S.sheet.adminGuide} icon={ShieldCheck}>
              {text('adminManualUrl', S.userManual.adminUrl, { placeholder: 'https://' })}
            </Block>
          </>
        )
      }

      case 'documentation':
        return <DocumentationCard />

      case 'notifications':
        /* A list to open, in the order the events come in — not a table to search. */
        return (
          <ul className="set-list">
            {[...(data?.notificationTemplates ?? [])]
              .sort((a, b) => (a.type ?? 0) - (b.type ?? 0))
              .map((template) => (
                <li key={template.id}>
                  <Link className="set-list__row is-link" to={`/settings/notifications/${template.id}`}>
                    <span className="set-list__name">
                      <bdi>{template.name}</bdi>
                    </span>
                    {template.lastUpdated && (
                      <span className="set-list__meta" dir="ltr">
                        {asDate(template.lastUpdated)}
                      </span>
                    )}
                    <ChevronLeft className="set-go ltr-flip" size={16} strokeWidth={2} aria-hidden="true" />
                  </Link>
                </li>
              ))}
          </ul>
        )

      case 'email':
        return (
          <>
            <Block title={S.sheet.connection} icon={Server}>
              {text('emailHost', S.email.host)}
              {text('emailPort', S.email.port, { hint: S.email.portHint, inputMode: 'numeric' })}
              {/* A choice, not a text box: only these five names mean anything to the mail
                  client, and anything else used to be stored and read as StartTls. */}
              <Row label={S.email.ssl}>
                {(fieldId) => (
                  <Select id={fieldId} value={form.emailSslOptions} onChange={set('emailSslOptions')} options={SSL_OPTIONS} />
                )}
              </Row>
              {text('emailUsername', S.email.username)}
              {text('emailPassword', S.email.password, { type: 'password' })}
              {textSwitch('disableCertValidation', S.email.skipCertCheck)}
            </Block>
            <Block title={S.sheet.sender} icon={Send}>
              {text('emailFrom', S.email.from, { hint: S.email.fromHint })}
              {text('emailSenderName', S.email.senderName, { dir: undefined })}
              {textSwitch('emailServiceState', S.email.serviceState)}
            </Block>
          </>
        )

      case 'messaging':
        /* One panel (2026-10-22): the note said once over a row per channel — the same
           warning in three panels read as three problems, and as a wall of text. */
        return (
          <Block title={S.nav.messaging} icon={MessagesSquare}>
            <InfoNote icon={TriangleAlert}>{S.messaging.unavailable}</InfoNote>
            {CHANNELS.map((channel) => (
              <ReadValue key={channel.id} label={S.messaging[channel.id]}>
                {S.messaging.off}
              </ReadValue>
            ))}
          </Block>
        )

      case 'reports':
        return (
          <>
            <Block title={S.reports.title} icon={ChartColumn}>
              {flag('userStatistics', S.reports.userStatistics)}
            </Block>
            <Block title={S.sheet.reportColumns} icon={Columns3}>
              <Matrix
                caption={S.sheet.reportColumns}
                head={S.sheet.field}
                columns={[
                  { id: 'policiesColumns', label: S.sheet.policiesReport },
                  { id: 'examsColumns', label: S.sheet.examsReport },
                ]}
                rows={S.profileAttributes.map((attribute) => ({
                  id: attribute.name,
                  label: attributeLabel(attribute),
                }))}
                cell={(row, column) => (
                  <Checkbox
                    id={`${column.id}-${row.id}`}
                    checked={(form[column.id] ?? []).includes(row.id)}
                    onChange={() => toggleColumn(column.id, row.id)}
                  >
                    <span className="sr-only">{`${row.label} — ${column.label}`}</span>
                  </Checkbox>
                )}
              />
            </Block>
          </>
        )

      default:
        return null
    }
  }

  const saves = Boolean(MAIN_SECTIONS[current])

  return (
    <SettingsTabs
      page="main"
      title={S.title}
      current={current}
      onLocal={nav.select}
      dirty={saves && settings.isDirty(current)}
      saving={settings.saving}
      onSave={saves ? () => settings.saveSection(current) : undefined}
      onDiscard={() => settings.resetSection(current)}
      focus={nav.focus}
      ready={settings.ready}
      blocked={
        /* A REFUSED LOAD IS NOT A SLOW ONE: a user without SettingsManagers used to watch the
           loader for as long as they cared to wait. */
        loadError ? (
          loadError.denied ? (
            <DeniedState description={loadError.message} />
          ) : (
            <ErrorState title={C.loadFailed} description={loadError.message} />
          )
        ) : null
      }
      extra={
        current === 'email' ? (
          <Button variant="quiet" onClick={() => setAskingTest(true)} busy={testing}>
            <Mails size={16} strokeWidth={1.75} aria-hidden="true" />
            {S.email.test}
          </Button>
        ) : current === 'identity' ? (
          <Button
            variant="quiet"
            onClick={() => {
              setValue('brandMain', DEFAULT_MAIN)
              setValue('brandActive', DEFAULT_ACTIVE)
            }}
          >
            <RotateCcw size={15} strokeWidth={1.75} aria-hidden="true" />
            {S.theme.restore}
          </Button>
        ) : null
      }
    >
      {settings.ready ? section(current) : <LoadingState />}

      {/* Where the probe lands — asked for, as the legacy screen asks. */}
      <PromptDialog
        open={askingTest}
        icon={Mails}
        title={S.email.testTitle}
        subtitle={S.email.title}
        label={S.email.testAddress}
        hint={S.email.testAddressHint}
        placeholder="name@example.com"
        type="email"
        dir="ltr"
        confirmLabel={S.email.testSend}
        busy={testing}
        onSubmit={runEmailTest}
        onClose={() => setAskingTest(false)}
      />
    </SettingsTabs>
  )
}
